import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { TableHTMLAttributes } from 'react';
import { border_radius_styles_with_size, semantic_colors } from '../../foundation';
import { buildCSSWithLength } from '../../styles';
import { Scroll } from '../table/types';

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /**
   * 테이블 스크롤 영역 너비/높이를 지정할 수 있습니다.
   *  - { x: number | string, y: number | string }
   *  - x: 가로 스크롤을 설정하고 스크롤 영역의 너비를 지정합니다.
   *  - y: 세로 스크롤을 설정하고 스크롤 영역의 높이를 지정합니다.
   *  - x 사용시 table-layout이 fixed 처리됩니다.(기본 auto)
   */
  scroll?: Scroll;
  /**
   * 테이블의 컬럼 리사이즈 활성화 여부
   *   - width값 지정된 컬럼은 resizable가 불가합니다.
   *   - table-layout이 fixed 처리됩니다.(기본 auto)
   * @default false
   */
  resizable?: boolean;
}

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ children, className, scroll, resizable, style, ...props }: TableProps, ref) => {
    return (
      <StyledTable
        ref={ref}
        className={classNames('pds-table', className)}
        scroll={scroll}
        resizable={resizable}
        style={style}
        {...props}
      >
        {children}
      </StyledTable>
    );
  },
);

const StyledTable = styled.table<{ scroll?: Scroll; resizable?: boolean }>`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  ${({ scroll }) =>
    scroll?.x
      ? `
      table-layout: fixed;
      ${scroll?.x && `${buildCSSWithLength('min-width', scroll.x ?? '100%')}`};
    `
      : 'table-layout: auto;'}
  ${({ resizable }) => resizable && `table-layout: fixed;`}

  thead {
    /** 마지막 헤더 행까지 이어지는 셀은 아래에 헤더 행이 없으므로 lastRow 로 제외합니다. */
    > tr:not(:last-of-type) > th:not(.pds-table-col-last-row) {
      background-image: linear-gradient(to right, transparent, ${semantic_colors.border.secondary}, transparent);
      background-position: bottom;
      background-repeat: no-repeat;
      background-size: 100% 1px;
    }

    > tr {
      > th {
        border-top: none;
        border-left: none;
        &:last-of-type:not(.show-right-border) {
          border-right: none;
        }
      }
    }

    > tr:first-of-type {
      > th:first-of-type {
        border-top-left-radius: ${border_radius_styles_with_size.small}px;
      }
      > th:last-of-type {
        border-top-right-radius: ${border_radius_styles_with_size.small}px;
      }
    }

    > tr:last-of-type {
      > th:first-of-type {
        border-bottom-left-radius: ${border_radius_styles_with_size.small}px;
      }
      > th:last-of-type {
        border-bottom-right-radius: ${border_radius_styles_with_size.small}px;
      }
    }

    > tr > th.pds-table-col-last-row:first-of-type {
      border-bottom-left-radius: ${border_radius_styles_with_size.small}px;
    }

    > tr > th.pds-table-col-last-row:last-of-type {
      border-bottom-right-radius: ${border_radius_styles_with_size.small}px;
    }

    &:has(> tr:not(:last-of-type) > th.pds-table-col-last-row:first-of-type) {
      > tr:last-of-type > th:first-of-type {
        border-bottom-left-radius: 0;
      }
    }

    &:has(> tr:not(:last-of-type) > th.pds-table-col-last-row:last-of-type) {
      > tr:last-of-type > th:last-of-type {
        border-bottom-right-radius: 0;
      }
    }
  }

  tbody {
    > tr {
      /** 라벨 셀(th)도 td 와 같은 구분선을 그려야 라벨/값 사이에서 선이 끊기지 않습니다. */
      > th {
        border-top: none;
        border-left: none;
        border-bottom: 1px solid ${semantic_colors.border.primary};
        /** rowSpan 셀은 부모 tr 이 마지막 행이 아니라 아래 :last-of-type 리셋이 못 잡습니다. */
        &.pds-table-col-last-row {
          border-bottom: none;
        }
      }
      &:last-of-type {
        > th,
        > td {
          border-bottom: none;
        }
      }
    }
  }

  /** 헤더가 없으면 첫 행이 상단 경계선을 그립니다. 직계(>)로 한정해야 중첩 테이블이 안 걸립니다. */
  > tbody > tr:first-of-type {
    > th,
    > td {
      border-top: 1px solid ${semantic_colors.border.primary};
    }
  }

  > thead + tbody > tr:first-of-type {
    > th,
    > td {
      border-top: none;
    }
  }
`;
